import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublishedVehicleResult } from 'fuel-benchmark-contracts';
import { Archive, ArrowLeft, GitCompare, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orpc } from '../../../apis/orpc-query';
import { Button } from '../../../components/ui/Button';
import { DefinitionList, type DefinitionItem, StatCard } from '../../../components/ui/DefinitionList';
import { Dialog } from '../../../components/ui/Dialog';
import { InfoCell } from '../../../components/ui/InfoCell';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusText } from '../../../components/ui/StatusText';
import { Tabs, type TabItem } from '../../../components/ui/Tabs';
import { formatCount, formatDay, formatDayTime, formatMeasurement, formatText } from '../../../lib/format';
import { serverMessage } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { PublishedResultSheet } from '../PublishedResultSheet';
import { powerOutput, powertrainTone, tirePressureSummary, vehicleStatusTone, vehicleTitle } from '../vehicle-display';
import { VehicleSheet } from '../VehicleSheet';
import { CoverageTab } from './CoverageTab';
import { TestHistoryTab } from './TestHistoryTab';

type DetailTab = 'overview' | 'specifications' | 'coverage' | 'history' | 'published';

export const VehicleDetailPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  const [tab, setTab] = useState<DetailTab>('overview');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPublishedOpen, setIsPublishedOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PublishedVehicleResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const vehicle = useQuery(orpc.vehicles.get.queryOptions({ input: { id }, enabled: id !== '' }));
  const attributeOptions = useQuery(orpc.vehicles.attributeOptions.queryOptions());

  const archiveVehicle = useMutation(orpc.vehicles.archive.mutationOptions());
  const deletePublishedResult = useMutation(orpc.vehicles.publishedResults.delete.mutationOptions());

  const segmentOptions = useMemo(
    () => (attributeOptions.data?.segments ?? []).map((segment) => ({ value: segment.id, label: segment.name })),
    [attributeOptions.data],
  );

  const tabs: readonly TabItem<DetailTab>[] = [
    { value: 'overview', label: t('vehicles.tabs.overview') },
    { value: 'specifications', label: t('vehicles.tabs.specifications') },
    { value: 'coverage', label: t('vehicles.tabs.coverage') },
    { value: 'history', label: t('vehicles.tabs.history') },
    { value: 'published', label: t('vehicles.tabs.published'), badge: vehicle.data?.publishedResults.length ?? 0 },
  ];

  if (vehicle.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner label={t('app.loading')} />
        {t('app.loading')}
      </div>
    );
  }

  if (vehicle.isError || !vehicle.data) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/vehicles')}
        >
          <ArrowLeft className="size-3.5" />
          {t('actions.back')}
        </Button>
        <div className="rounded-lg border border-border bg-card p-8">
          <p className="text-sm text-muted-foreground">{t('vehicles.notFound')}</p>
        </div>
      </div>
    );
  }

  const data = vehicle.data;

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
    ]);
  };

  const onArchive = async (): Promise<void> => {
    setActionError(null);

    try {
      await archiveVehicle.mutateAsync({ id: data.id });
      await invalidate();
      setIsArchiveOpen(false);
    } catch (error) {
      setActionError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  };

  const onDeletePublished = async (): Promise<void> => {
    if (!pendingDelete) {
      return;
    }

    setActionError(null);

    try {
      await deletePublishedResult.mutateAsync({ vehicleId: data.id, id: pendingDelete.id });
      await queryClient.invalidateQueries({ queryKey: orpc.vehicles.get.key() });
      setPendingDelete(null);
    } catch (error) {
      setActionError(serverMessage(error) ?? t('errors.saveFailed'));
    }
  };

  const identityItems: DefinitionItem[] = [
    { label: t('vehicles.form.code'), value: <span className="font-mono text-xs">{data.code}</span> },
    { label: t('vehicles.form.manufacturer'), value: data.manufacturer },
    { label: t('vehicles.form.model'), value: data.model },
    { label: t('vehicles.form.modelYear'), value: data.modelYear },
    { label: t('vehicles.form.trim'), value: formatText(data.trim) },
    { label: t('vehicles.form.vin'), value: <span className="font-mono text-xs">{formatText(data.vin)}</span> },
    { label: t('vehicles.form.segment'), value: data.segmentName },
    {
      label: t('vehicles.overview.status'),
      value: (
        <StatusText
          label={t(`enums.status.${data.status}`)}
          tone={vehicleStatusTone(data.status)}
        />
      ),
    },
  ];

  const powertrainItems: DefinitionItem[] = [
    {
      label: t('vehicles.form.powertrainType'),
      value: (
        <StatusText
          label={t(`enums.powertrain.${data.powertrainType}`)}
          tone={powertrainTone(data.powertrainType)}
        />
      ),
    },
    { label: t('vehicles.form.engine'), value: formatText(data.engine) },
    { label: t('vehicles.form.powerHp'), value: data.powerHp === null ? t('common.notSet') : formatMeasurement(data.powerHp, 'hp', 0) },
    { label: t('vehicles.form.torqueNm'), value: data.torqueNm === null ? t('common.notSet') : formatMeasurement(data.torqueNm, 'Nm', 0) },
    { label: t('vehicles.form.transmission'), value: formatText(data.transmission) },
    { label: t('vehicles.form.drivetrain'), value: formatText(data.drivetrain) },
    { label: t('vehicles.form.fuelType'), value: formatText(data.fuelType) },
    { label: t('vehicles.form.defaultFuelGrade'), value: formatText(data.defaultFuelGrade) },
  ];

  const chassisItems: DefinitionItem[] = [
    { label: t('vehicles.form.tireSize'), value: formatText(data.tireSize) },
    { label: t('vehicles.specs.tirePressure'), value: formatText(tirePressureSummary(data.oemTirePressureKpa)) },
    { label: t('vehicles.form.odometerKm'), value: data.odometerKm === null ? t('common.notSet') : formatCount(data.odometerKm) },
    { label: t('vehicles.form.notes'), value: formatText(data.notes) },
    { label: t('vehicles.overview.added'), value: formatDayTime(data.createdAt) },
    { label: t('vehicles.overview.updated'), value: formatDayTime(data.updatedAt) },
  ];

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/vehicles')}
      >
        <ArrowLeft className="size-3.5" />
        {t('nav.vehicles')}
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-foreground">{vehicleTitle(data)}</h1>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{data.code}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Hands the compare screen a half-started comparison: this vehicle picked, the second one to go. */}
          <Button
            variant="outline"
            onClick={() => navigate(`/compare?vehicleIds=${data.id}`)}
          >
            <GitCompare className="size-4" />
            {t('vehicles.compare')}
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsEditOpen(true)}
          >
            <Pencil className="size-4" />
            {t('vehicles.edit')}
          </Button>
          {data.status === 'ACTIVE' ? (
            <Button
              variant="outline"
              onClick={() => setIsArchiveOpen(true)}
            >
              <Archive className="size-4" />
              {t('vehicles.archive')}
            </Button>
          ) : null}
        </div>
      </div>

      {actionError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </p>
      ) : null}

      <Tabs
        items={tabs}
        value={tab}
        onChange={setTab}
      />

      {tab === 'overview' ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label={t('vehicles.overview.status')}
              value={
                <StatusText
                  label={t(`enums.status.${data.status}`)}
                  tone={vehicleStatusTone(data.status)}
                />
              }
              hint={data.segmentName}
            />
            <StatCard
              label={t('vehicles.form.powertrainType')}
              value={
                <StatusText
                  label={t(`enums.powertrain.${data.powertrainType}`)}
                  tone={powertrainTone(data.powertrainType)}
                />
              }
              hint={formatText(data.engine)}
            />
            <StatCard
              label={t('vehicles.overview.powerOutput')}
              value={formatText(powerOutput(data))}
              hint={formatText(data.transmission)}
            />
            <StatCard
              label={t('vehicles.overview.publishedCount')}
              value={formatCount(data.publishedResults.length)}
              hint={formatText(data.defaultFuelGrade)}
            />
          </div>

          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-medium text-card-foreground">{t('vehicles.overview.title')}</h2>
            <DefinitionList
              className="mt-2"
              items={[
                { label: t('vehicles.overview.drivetrain'), value: formatText(data.drivetrain) },
                { label: t('vehicles.overview.fuel'), value: formatText([data.fuelType, data.defaultFuelGrade].filter(Boolean).join(' · ')) },
                { label: t('vehicles.form.tireSize'), value: formatText(data.tireSize) },
                { label: t('vehicles.overview.odometer'), value: data.odometerKm === null ? t('common.notSet') : formatCount(data.odometerKm) },
                { label: t('vehicles.overview.added'), value: formatDay(data.createdAt) },
                { label: t('vehicles.overview.notes'), value: formatText(data.notes) },
              ]}
            />
          </section>
        </div>
      ) : null}

      {tab === 'specifications' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-medium text-card-foreground">{t('vehicles.specs.identity')}</h2>
            <DefinitionList
              className="mt-2"
              items={identityItems}
            />
          </section>
          <section className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-medium text-card-foreground">{t('vehicles.specs.powertrain')}</h2>
            <DefinitionList
              className="mt-2"
              items={powertrainItems}
            />
          </section>
          <section className="rounded-lg border border-border bg-card p-5 lg:col-span-2">
            <h2 className="text-sm font-medium text-card-foreground">{t('vehicles.specs.chassis')}</h2>
            <DefinitionList
              className="mt-2"
              items={chassisItems}
            />
          </section>
        </div>
      ) : null}

      {tab === 'coverage' ? <CoverageTab vehicleId={data.id} /> : null}

      {tab === 'history' ? <TestHistoryTab vehicleId={data.id} /> : null}

      {tab === 'published' ? (
        <section className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="max-w-2xl text-sm text-muted-foreground">{t('vehicles.published.subtitle')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPublishedOpen(true)}
            >
              <Plus className="size-3.5" />
              {t('vehicles.published.add')}
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  {[
                    t('vehicles.published.regime'),
                    t('vehicles.published.metric'),
                    t('vehicles.published.value'),
                    t('vehicles.published.source'),
                    t('vehicles.published.market'),
                    '',
                  ].map((header, index) => (
                    <th
                      key={`${header}-${index}`}
                      scope="col"
                      className="whitespace-nowrap px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.publishedResults.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-muted-foreground"
                    >
                      {t('vehicles.published.empty')}
                    </td>
                  </tr>
                ) : (
                  data.publishedResults.map((result) => (
                    <tr
                      key={result.id}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="px-4 py-3 text-start">
                        <StatusText
                          label={t(`enums.regime.${result.regime}`)}
                          tone="muted"
                        />
                      </td>
                      <td className="px-4 py-3 text-start font-mono text-xs text-muted-foreground">{result.metric}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-start font-medium text-foreground">
                        {formatMeasurement(result.value, result.unit)}
                      </td>
                      <td className="px-4 py-3 text-start">
                        <InfoCell
                          title={formatText(result.source)}
                          subline={result.sourceDate}
                        />
                      </td>
                      <td className="px-4 py-3 text-start text-muted-foreground">{formatText(result.market)}</td>
                      <td className="px-4 py-3 text-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('actions.delete')}
                          onClick={() => setPendingDelete(result)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <VehicleSheet
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        segmentOptions={segmentOptions}
        vehicle={data}
      />

      <PublishedResultSheet
        isOpen={isPublishedOpen}
        onClose={() => setIsPublishedOpen(false)}
        vehicleId={data.id}
      />

      <Dialog
        isOpen={isArchiveOpen}
        onClose={() => setIsArchiveOpen(false)}
        title={t('vehicles.archiveDialog.title')}
        description={t('vehicles.archiveDialog.body', { code: data.code })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsArchiveOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              isLoading={archiveVehicle.isPending}
              onClick={() => void onArchive()}
            >
              {t('vehicles.archiveDialog.confirm')}
            </Button>
          </>
        }
      />

      <Dialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={t('vehicles.published.deleteTitle')}
        description={
          pendingDelete
            ? t('vehicles.published.deleteBody', {
                regime: pendingDelete.regime,
                value: pendingDelete.value,
                unit: pendingDelete.unit,
                code: data.code,
              })
            : undefined
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setPendingDelete(null)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              isLoading={deletePublishedResult.isPending}
              onClick={() => void onDeletePublished()}
            >
              {t('actions.delete')}
            </Button>
          </>
        }
      />
    </div>
  );
};
